import { Box, Button, Flex, Input, InputGroup, InputLeftElement, Text } from "@chakra-ui/react";
import React, { useEffect } from "react";
import { RiAddFill, RiSearch2Fill } from "react-icons/ri";
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { getAllChaptersRequest } from "../../../modules/admin-modules/chapters/Actions";
import { openCreateQuestionModel } from "../../../modules/admin-modules/models/Actions";

const C = {
  primary: "#6A1E1E",
  secondary: "#4A0F1F",
  border: "#D8CEC3",
  muted: "rgba(30, 27, 23, 0.6)",
};

const selectStyles = {
  control: (styles, state) => ({
    ...styles,
    width: "100%",
    minHeight: "42px",
    backgroundColor: "rgba(255,255,255,0.9)",
    borderColor: state.isFocused ? C.primary : C.border,
    borderWidth: "1px",
    borderRadius: "9999px",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(106, 30, 30, 0.12)" : "none",
    ":hover": { borderColor: C.primary },
  }),
  indicatorSeparator: () => ({ display: "none" }),
  placeholder: (styles) => ({
    ...styles,
    color: C.muted,
    fontWeight: 600,
    fontSize: "14px",
  }),
  singleValue: (styles) => ({
    ...styles,
    color: C.secondary,
    fontWeight: 700,
    fontSize: "14px",
  }),
  menu: (styles) => ({
    ...styles,
    borderRadius: "16px",
    border: `1px solid ${C.border}`,
    boxShadow: "0 16px 40px rgba(74, 15, 31, 0.18)",
    zIndex: 20,
  }),
  option: (styles, state) => ({
    ...styles,
    borderRadius: "12px",
    fontWeight: state.isSelected ? 800 : 600,
    color: state.isSelected ? "#E9E2DB" : C.secondary,
    backgroundColor: state.isSelected
      ? C.secondary
      : state.isFocused
        ? "rgba(233, 226, 219, 0.7)"
        : "transparent",
  }),
};

const SearchBar = ({ setAdd, setSelectedData, setChapterId, searchTerm, setSearchTerm }) => {
  const dispatch = useDispatch();
  const count = useSelector((state) => state.questionsBank.count);
  const chapters = useSelector((state) => state.chapters.chapters);

  const chaptersOptions = chapters
    ?.filter((c) => Number(c?.active) === 0) // filter active == 0
    .map((c) => ({ value: c.id, label: c.name }));

  useEffect(() => {
    if (!chapters?.length) dispatch(getAllChaptersRequest());
  }, [chapters, dispatch]);

  return (
    <Box px={{ base: 4, md: 8 }} pt={6} pb={0} w="100%">
      <Flex
        align={{ base: "flex-start", md: "flex-end" }}
        justify="space-between"
        gap={6}
        flexDir={{ base: "column", md: "row" }}
        mb={6}
      >
        <Box>
          <Text
            fontSize={{ base: "32px", md: "54px" }}
            fontWeight="800"
            color={C.primary}
            fontFamily="'Amiri', 'Tajawal', sans-serif"
            lineHeight="1"
          >
            Question bank
          </Text>
          <Text mt={3} color={C.muted} fontSize="sm" maxW="lg">
            Choose a chapter to load questions, then search and manage items in the table.
          </Text>
          <Text mt={2} fontSize="sm" fontWeight="700" color={C.secondary}>
            Questions (loaded): {count ?? 0}
          </Text>
        </Box>

        <Button
          borderRadius="full"
          leftIcon={<RiAddFill />}
          bg={C.primary}
          color="#FAF2EB"
          px={8}
          py={6}
          fontWeight="800"
          _hover={{ bg: C.secondary }}
          onClick={() => {
            setSelectedData({});
            setAdd(true);
            dispatch(openCreateQuestionModel());
          }}
        >
          Add question
        </Button>
      </Flex>

      <Flex
        gap={4}
        mb={6}
        flexDir={{ base: "column", md: "row" }}
        align={{ base: "stretch", md: "flex-end" }}
      >
        <Box flex="1" maxW={{ md: "320px" }}>
          <Text fontSize="xs" fontWeight="800" color={C.primary} textTransform="uppercase" letterSpacing="0.1em" mb={2}>
            Chapter filter
          </Text>
          <Select
            options={chaptersOptions}
            onChange={(opt) => setChapterId(opt?.value)}
            styles={selectStyles}
            placeholder="Select chapter…"
            isClearable
          />
        </Box>
        <Box flex="1" maxW="520px">
          <InputGroup>
            <InputLeftElement pointerEvents="none" h="full">
              <RiSearch2Fill color="rgba(106, 30, 30, 0.5)" fontSize="18px" />
            </InputLeftElement>
            <Input
              placeholder="Search in loaded questions…"
              value={searchTerm}
              borderRadius="full"
              border="1px solid"
              borderColor={C.border}
              bg="rgba(255,255,255,0.85)"
              pl={10}
              _focus={{ borderColor: C.primary, boxShadow: "0 0 0 1px rgba(106, 30, 30, 0.2)" }}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Box>
      </Flex>
    </Box>
  );
};

export default SearchBar;
